import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import {
  AlertTriangle, CheckCircle2, ArrowRight, ShieldCheck, Activity,
  FileText, Sigma, Lock, ExternalLink
} from 'lucide-react';

const DOESNT_WORK = [
  { n: 1,  problem: 'AI initiatives emerge bottom-up from innovation or IT, with no business ownership or accountability.',
    fix: 'ACS\u2122 lives where credit committees already work: owned by underwriting, not innovation lab. P&L attribution is immediate.' },
  { n: 2,  problem: 'Success measured in pilots and demos rather than cost, risk, revenue, or customer impact.',
    fix: 'Every Checkpoint settles atomically in USDC on Base. There is no "demo." Either the line paid out or it didn\u2019t.' },
  { n: 3,  problem: 'GenAI treated as a silver bullet: chatbots and copilots dominate while real ops gaps remain.',
    fix: 'Foundational rule: the LLM never touches the math. Dollar figures, scores, and risk are produced only by deterministic NumPy on verified contracts.' },
  { n: 4,  problem: 'AI applied to isolated tasks instead of rethinking end-to-end processes.',
    fix: 'Kojiru is the end-to-end credit primitive: underwriting (ACS\u2122) \u2192 projection (Yield Simulator) \u2192 settlement (per-task escrow) \u2192 repayment (Checkpoint) \u2192 recourse (WithdrawalGuard).' },
  { n: 5,  problem: 'Models inform humans rather than act in workflows: review steps multiply, friction grows.',
    fix: 'Smart contracts on Base execute settlement atomically. Decision and dollar movement are the same transaction. Zero review queues.' },
  { n: 6,  problem: 'Use cases are narrow and duplicated across teams, functions, and regions.',
    fix: 'One Kojiru integration covers every agent the institution lends to: across desks, regions, customer segments. One primitive, infinite agents.' },
  { n: 7,  problem: 'AI sits outside core banking, payments, and risk platforms: automation stays shallow.',
    fix: 'Kojiru is the core. USDC settlement IS the payment rail. The verified smart contracts ARE the risk engine. The Checkpoint log IS the audit trail.' },
  { n: 8,  problem: 'Data quality and ownership not tested early: problems surface during deployment, patched locally.',
    fix: 'Checkpoints are deterministic, on-chain, immutable structured data by design. The data is correct or the transaction reverts. No late-stage data hygiene crisis.' },
  { n: 9,  problem: 'Governance and model risk engaged late: deployment slows, friction grows.',
    fix: 'BaseScan source-verified contracts on Base, with formal-verification specs authored and symbolic-execution tests configured. Production-stack third-party formal verification is on our Q3 2026 roadmap. Governance IS the math.' },
  { n: 10, problem: 'Scaling slow and costly due to tight coupling with local processes and systems.',
    fix: 'Plug-and-play SDK wrappers for popular agent frameworks. Each new agent integration is a config change, not a project.' },
];

const WORKS = [
  { n: 1,  principle: 'AI tied to clear business priorities: explicit assumptions on cost, risk, revenue, or service.',
    delivery: 'Explicit dollar yield, default probability, alpha vs. idle USDC. Each output names the line item it moves.' },
  { n: 2,  principle: 'Management owns AI outcomes and is accountable for delivery and value.',
    delivery: 'ACS\u2122 score deltas are unambiguous P&L signals; credit committees defend numbers in board packs without translation.' },
  { n: 3,  principle: 'Processes simplified first, AI introduced only where it materially improves outcomes.',
    delivery: 'Bilateral credit lines remove underwriting bureaucracy. No pooled-fund opacity. Cleaner workflow before the math arrives.' },
  { n: 4,  principle: 'GenAI used selectively, combined with rules / classical ML / decisioning where predictability matters.',
    delivery: 'LLM = language only. Math = math only. The Bounded Logic Graph thesis in one sentence.' },
  { n: 5,  principle: 'AI outputs embedded into workflows with defined human control points.',
    delivery: 'Covenant tiers (Strict / Standard / Loose), WithdrawalGuard limits, per-task escrow caps. Controls in the contract.' },
  { n: 6,  principle: 'Agent-based approaches used selectively to coordinate complex processes, not as a universal model.',
    delivery: 'Kojiru is the selective agent-finance primitive. Banks adopt it for one thing, credit to agents, not for everything.' },
  { n: 7,  principle: 'Data improvements driven by priority use cases, with clarified ownership where it matters.',
    delivery: 'Every Checkpoint deepens the ACS\u2122 dataset. Data quality compounds with usage. Ownership is structurally on-chain.' },
  { n: 8,  principle: 'Risk and compliance involved early to define controls and acceptable boundaries.',
    delivery: 'Formal verification is pre-deployment. The math itself is the compliance signal. WithdrawalGuard caps risk by design.' },
  { n: 9,  principle: 'Scaling by extending proven use cases to similar products or regions.',
    delivery: 'SDK wrappers are the proven pattern. Same engine, new agent universe. Zero re-architecture per integration.' },
  { n: 10, principle: 'Impact measured against prior performance: ROI quantified and benchmarked to KPIs.',
    delivery: 'Yield Simulator vs. USDC staking baseline = explicit benchmark. Alpha is the headline number, not an afterthought.' },
  { n: 11, principle: 'AI operational in specific domains rather than a bank-wide transformation narrative.',
    delivery: 'Kojiru is only agent credit. A domain, not a religion. Adopt for one thing; scale within the domain on your timeline.' },
];

export default function ForBanks() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="for-banks-page">
      <Navbar />

      {/* ── J.D. POWER TRUST-PARITY RIBBON ── */}
      <section className="pt-32 pb-8 px-6" data-testid="jdpower-trust-parity-ribbon">
        <div className="container mx-auto max-w-6xl">
          <div className="border border-amber-500/30 rounded-2xl bg-amber-500/5 p-6 md:p-8">
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-3">
              J.D. POWER &middot; BANKING &amp; PAYMENTS INTELLIGENCE REPORT &middot; JULY 2026
            </div>
            <div className="grid md:grid-cols-4 gap-4 mb-5">
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">40%</div>
                <div className="text-sm text-slate-300">of Americans have asked AI for help with their money</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">34%</div>
                <div className="text-sm text-slate-300">credit AI with smarter financial decisions</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">35%</div>
                <div className="text-sm text-slate-300">credit their bank with the same</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">66%</div>
                <div className="text-sm text-slate-300">of Americans are financially unhealthy</div>
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Trust parity between AI and banks is not coming. It is measured, and it is here.
              The consumer segment your bank most needs to retain: younger, financially
              strained, already reaching for AI money help: is the segment most likely to
              progress from AI advice to AI-mediated payments in the next twelve months. The
              recourse layer is what makes that progression legally deployable inside your
              product surface. That is what Kojiru builds.
            </p>
            <div className="text-[10px] text-slate-500 mt-3 tracking-[1.4px]">
              SOURCE: J.D. POWER BANKING AND PAYMENTS INTELLIGENCE REPORT, 27 JULY 2026 &middot; JDPOWER.COM
            </div>
          </div>
        </div>
      </section>

      {/* ── DIRECT-TO-AGENT DISTRIBUTION RIBBON ── */}
      <section className="py-8 px-6" data-testid="direct-distribution-ribbon">
        <div className="container mx-auto max-w-6xl">
          <div className="border border-cyan-500/30 rounded-2xl bg-cyan-500/5 p-6 md:p-8">
            <div className="text-[10px] font-bold tracking-[2.4px] text-cyan-300 mb-3">
              THE DIRECT-TO-AGENT DISTRIBUTION CHANNEL &middot; SEPTEMBER 2026
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-5">
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">+60&ndash;72%</div>
                <div className="text-sm text-slate-300">projected incremental gross revenue for the two largest US card schemes at full agentic-tokenization adoption</div>
              </div>
              <div>
                <div className="text-lg font-black text-white leading-tight mb-2">Read-only &rarr; Review-and-Release &rarr; Bounded autonomy</div>
                <div className="text-sm text-slate-300">the prescribed defensive framework for banks: a three-phase ladder</div>
              </div>
              <div>
                <div className="text-lg font-black text-white leading-tight mb-2">Same three-phase ladder</div>
                <div className="text-sm text-slate-300">the exact framework the <code className="text-cyan-300 text-xs">kojiru-recourse-blueprint</code> has shipped since September 2026</div>
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              An independent senior payments-industry analyst modeled the two largest US card schemes standing to capture roughly <strong className="text-white">+60&ndash;72% incremental gross revenue</strong> by distributing tokenized credentials <em className="not-italic text-white">directly</em> to LLMs, personal agents, and digital wallets: bypassing issuing banks and core processors. The prescribed defensive framework is a Read-only &rarr; Review-and-Release &rarr; Bounded-autonomy ladder. <strong className="text-white">When distribution is direct to the customer&rsquo;s agent, the FI&rsquo;s job is not to gate the transaction: it&rsquo;s to prove the pledge behind it.</strong> KYPA is the pledge layer under whatever tokenization service the schemes ship. Approving on the strength of a session or a token the agent is holding is inference. A pre-pledge on-chain is evidence.
            </p>
            <div className="text-[10px] text-slate-500 mt-3 tracking-[1.4px]">
              SOURCE: INDEPENDENT SENIOR PAYMENTS-INDUSTRY ANALYST (25+ YEARS), LINKEDIN COMMENTARY, 23 SEPTEMBER 2026 &middot; INSTITUTIONAL DESCRIPTOR ONLY PER NAMING POLICY
            </div>
          </div>
        </div>
      </section>

      {/* ── SEPT 22 REPRICING EVENT RIBBON ── */}
      <section className="py-8 px-6" data-testid="repricing-event-ribbon">
        <div className="container mx-auto max-w-6xl">
          <div className="border border-rose-500/30 rounded-2xl bg-rose-500/5 p-6 md:p-8">
            <div className="text-[10px] font-bold tracking-[2.4px] text-rose-300 mb-3">
              THE FIRST PUBLIC AGENTIC-DISINTERMEDIATION REPRICING EVENT &middot; 22 SEPTEMBER 2026
            </div>
            <div className="grid md:grid-cols-4 gap-4 mb-5">
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">&minus;2%</div>
                <div className="text-sm text-slate-300">S&amp;P 500 financial sector: one trading session</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">&minus;6%+</div>
                <div className="text-sm text-slate-300">largest US retail brokerage &amp; largest US independent broker-dealer, both same session</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white leading-none mb-2">&minus;4.57%</div>
                <div className="text-sm text-slate-300">world&rsquo;s largest online-travel platform, same session</div>
              </div>
              <div>
                <div className="text-lg font-black text-white leading-tight mb-2">Surgical, not macro</div>
                <div className="text-sm text-slate-300">the Nasdaq set a record the same day: only the AI-disintermediation-exposed names repriced</div>
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              On <strong className="text-white">September 22, 2026</strong>, the S&amp;P 500 financial sector fell 2% on a single trading session while the Nasdaq set a record (with the largest US retail brokerage, the largest US independent broker-dealer, and the world&rsquo;s largest online-travel platform all repricing 4-6% lower) on the news of a rapid-adoption AI-agent app rollout. That is the <strong className="text-white">first public market-repricing event tied specifically to agent-mediated disintermediation of financial services.</strong> Not directional forecast. Actual sector-level equity repricing. When distribution is direct to the customer&rsquo;s agent, the FI&rsquo;s job is not to gate the transaction: it&rsquo;s to prove the pledge behind it.
            </p>
            <div className="text-[10px] text-slate-500 mt-3 tracking-[1.4px]">
              SOURCE: LEADING US BUSINESS DAILY, 22 SEPTEMBER 2026 (4:47 PM ET) &middot; INSTITUTIONAL DESCRIPTOR ONLY PER NAMING POLICY
            </div>
          </div>
        </div>
      </section>

      {/* ── DETECTION vs RESPONSE RIBBON (bank-comms-voice institutional co-sign, Sep 26 2026) ── */}
      <section className="py-8 px-6" data-testid="detection-response-ribbon">
        <div className="container mx-auto max-w-6xl">
          <div className="border border-emerald-500/30 rounded-2xl bg-emerald-500/5 p-6 md:p-8">
            <div className="text-[10px] font-bold tracking-[2.4px] text-emerald-300 mb-3">
              DETECTION vs. RESPONSE &middot; A CHIEF COMMUNICATIONS OFFICER IN BANKING &amp; CRITICAL FINANCIAL INFRASTRUCTURE &middot; SEPTEMBER 2026
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-5">
              <div data-testid="detection-response-quote-a">
                <div className="text-xl sm:text-2xl font-black text-white leading-snug mb-3">
                  &ldquo;Detection protects the balance.<br />Response protects the relationship.&rdquo;
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  An operator with a global corporate-affairs remit covering cyber risk, financial crime, and operational resilience published the reframe in September 2026. The framing is bit-for-bit the primitive gap Kojiru occupies. Every four-row stack (identity &middot; intent &middot; attestation &middot; settlement) is <strong className="text-white">detection</strong>. Row 5 is <strong className="text-white">response</strong>.
                </p>
              </div>
              <div data-testid="detection-response-quote-b">
                <div className="text-lg sm:text-xl font-black text-white leading-snug mb-3">
                  &ldquo;<span className="text-emerald-200">&lsquo;You authorized this transaction&rsquo;</span> may be technically accurate. It is reputationally radioactive.&rdquo;
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  That is the sentence every FI hands a customer whose agent booked a $600 hotel on a $200 mandate. Existing recourse is a <strong className="text-white">10-minute window</strong>. Agentic commerce with faster payments is a <strong className="text-white">15-second window</strong>. Kojiru is the response layer sized to the collapsed clock: a signed mandate and a public operator pledge are on record before the second phone call arrives. The pledge is a public commitment signal, not a promise of repayment.
                </p>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-3 tracking-[1.4px]">
              SOURCE: A CHIEF COMMUNICATIONS OFFICER IN BANKING &amp; CRITICAL FINANCIAL INFRASTRUCTURE, PUBLIC LINKEDIN POST, 26 SEPTEMBER 2026 &middot; INSTITUTIONAL DESCRIPTOR ONLY PER NAMING POLICY
            </div>
          </div>
        </div>
      </section>

      {/* Chargeback-machine quote strip */}
      <section className="py-10 px-6 bg-slate-900/60 border-y border-white/10" data-testid="chargeback-machine-strip">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-lg sm:text-xl font-black text-white leading-snug mb-3">
            &ldquo;If you don&rsquo;t bind deterministic proof at authorization, your autonomous agent is just a chargeback machine.&rdquo;
          </p>
          <p className="text-xs text-slate-500 tracking-[1.4px]">
            A FINTECH FOUNDER WRITING PUBLICLY ON PAYMENT-NETWORK LIABILITY &middot; SEPTEMBER 2026
          </p>
        </div>
      </section>

      {/* ── HERO ── */}
      <section className="pt-8 pb-14 px-6 border-b border-white/[0.06]">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-2 mb-6 text-[11px] font-mono tracking-[0.22em] text-emerald-400">
            <span className="px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20">
              FOR BANKS · INSTITUTIONAL OPERATORS
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
            The bank-AI failures are{' '}
            <span className="text-amber-300">a confession.</span><br />
            We built the discipline they confess to missing.
          </h1>

          <p className="text-base sm:text-lg text-zinc-400 max-w-3xl mt-5 leading-relaxed">
            Every bank-AI initiative that has failed in the last three years traces back
            to the same architectural choice: a non-deterministic LLM in a decision loop the
            model-risk committee cannot defend. <span className="text-white font-semibold">Kojiru is built against that choice from line one.</span>
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <Link
              to="/langchain"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 text-black font-semibold text-sm hover:brightness-110 transition"
              data-testid="banks-cta-brief"
            >
              <FileText className="w-4 h-4" /> Request the Integration Brief
            </Link>
            <a
              href="/kojiru-yield-simulator-explainer.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/15 hover:bg-white/[0.04] text-sm font-semibold transition"
              data-testid="banks-cta-explainer"
            >
              <ExternalLink className="w-4 h-4" /> Yield Simulator math (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* ── DOESN'T WORK BLOCK ── */}
      <section className="py-16 px-6" data-testid="banks-doesnt-work-section">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-10">
            <div className="text-[11px] font-mono tracking-[0.22em] text-red-400 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> // WHERE BANK AI FAILS
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">
              The 10 failure patterns, mapped to the Kojiru fix.
            </h2>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              Drawn from a consulting-style banking AI implementation framework currently circulating. Every left column item is a downstream symptom of the same upstream architectural mistake.
            </p>
          </div>

          <div className="space-y-3" data-testid="banks-doesnt-work-grid">
            {DOESNT_WORK.map((item) => (
              <motion.div
                key={item.n}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-1 md:grid-cols-[3rem_1fr_1fr] gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]"
                data-testid={`banks-doesnt-work-row-${item.n}`}
              >
                <div className="text-2xl font-mono font-bold text-zinc-500">{String(item.n).padStart(2, '0')}</div>
                <div className="border-l-2 border-red-500/40 pl-4">
                  <div className="text-[10px] font-mono tracking-[0.2em] text-red-400 uppercase mb-1">Failure</div>
                  <p className="text-sm text-zinc-300 leading-relaxed">{item.problem}</p>
                </div>
                <div className="border-l-2 border-emerald-500/40 pl-4">
                  <div className="text-[10px] font-mono tracking-[0.2em] text-emerald-400 uppercase mb-1">Kojiru fix</div>
                  <p className="text-sm text-zinc-200 leading-relaxed">{item.fix}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DISCIPLINE BLOCK ── */}
      <section className="py-16 px-6 border-y border-white/[0.06] bg-gradient-to-b from-transparent via-emerald-500/[0.025] to-transparent">
        <div className="container mx-auto max-w-5xl">
          <div className="text-[11px] font-mono tracking-[0.22em] text-amber-400 mb-3 flex items-center gap-2">
            <Sigma className="w-3.5 h-3.5" /> // THE ARCHITECTURAL DISCIPLINE
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-5">
            Separate the math layer from the language layer.
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8 max-w-3xl">
            One non-negotiable rule, enforced upstream. No dollar figure, no risk score, no
            decision threshold, no compliance signal ever comes out of an LLM. The LLM is
            confined to one job: turning the deterministic outputs of the math layer into
            human-readable language. That single rule retires roughly nine of the ten failure
            patterns above before a project even kicks off.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="p-6 rounded-2xl bg-amber-500/[0.04] border border-amber-500/20">
              <div className="text-[10px] font-mono tracking-[0.22em] text-amber-400 uppercase mb-2">Layer 1</div>
              <h3 className="text-lg font-bold mb-3">The Math Layer</h3>
              <ul className="space-y-2 text-sm text-zinc-300 leading-relaxed">
                <li>· Agent Credit Score (ACS)™: deterministic, on-chain reputation</li>
                <li>· Yield Simulator: 10,000-path Monte Carlo, ~30 ms, reproducible</li>
                <li>· Per-task escrow + WithdrawalGuard: atomic settlement, rate-limited</li>
                <li>· BaseScan source-verified on Base: production-stack formal verification on Q3 2026 roadmap</li>
              </ul>
            </div>
            <div className="p-6 rounded-2xl bg-cyan-500/[0.04] border border-cyan-500/20">
              <div className="text-[10px] font-mono tracking-[0.22em] text-cyan-400 uppercase mb-2">Layer 2</div>
              <h3 className="text-lg font-bold mb-3">The Language Layer</h3>
              <ul className="space-y-2 text-sm text-zinc-300 leading-relaxed">
                <li>· LLM (a frontier LLM) writes the one-sentence summary at the end</li>
                <li>· No dollar figures, no risk scores, no decisions ever flow through the model</li>
                <li>· Pure language polishing on top of deterministic math</li>
                <li>· The only place the LLM is actually best in class</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── WORKS BLOCK ── */}
      <section className="py-16 px-6" data-testid="banks-works-section">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-10">
            <div className="text-[11px] font-mono tracking-[0.22em] text-emerald-400 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> // WHERE IT WORKS
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">
              All 11 &ldquo;works&rdquo; principles: delivered out of the box.
            </h2>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              The same framework&apos;s &ldquo;where it works&rdquo; column reads like a Kojiru product spec. Adopt the protocol and 11 of 11 boxes tick from day one.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="banks-works-grid">
            {WORKS.map((item) => (
              <div
                key={item.n}
                className="p-5 rounded-xl bg-emerald-500/[0.04] border border-emerald-500/20"
                data-testid={`banks-works-row-${item.n}`}
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex-shrink-0 w-7 h-7 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-mono font-bold flex items-center justify-center">
                    {String(item.n).padStart(2, '0')}
                  </div>
                  <h3 className="text-sm font-semibold text-emerald-200 leading-snug">{item.principle}</h3>
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed pl-10">{item.delivery}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LATENT-CASH COMPANION CALLOUT ── */}
      <section className="py-14 px-6 border-t border-white/[0.06]" data-testid="latent-cash-callout">
        <div className="container mx-auto max-w-4xl">
          <div className="border-l-4 border-amber-500 pl-6 md:pl-8 py-2">
            <div className="text-[10px] font-bold tracking-[2.4px] text-amber-400 mb-3">
              THE LATENT-CASH COMPANION &middot; INDUSTRY-ANALYST FRAMING, SEPTEMBER 2026
            </div>
            <p className="text-xl sm:text-2xl font-black text-white leading-snug mb-4">
              An agent optimizing 24/7 doesn&rsquo;t just remove latent cash from the system: it means <span className="text-amber-300">24/7 exposure</span> to the agent misfiring inside its bounded mandate.
            </p>
            <p className="text-base text-slate-300 leading-relaxed">
              Pledged collateral is what turns Bounded autonomy into a <strong className="text-white">collectible operating mode.</strong> Every breach is recorded publicly next to the pledge. Sits under whichever tokenization service or brokerage workflow the incumbents ship. Authentication proves what happened. The pledge shows who stood behind it. Both live in one credential.
            </p>
            <p className="text-xs text-slate-500 mt-4 tracking-[1.4px]">
              FRAMING DRAWN FROM SEPTEMBER 2026 INDUSTRY-ANALYST COMMENTARY ON AGENT-MEDIATED FINANCE &middot; INSTITUTIONAL DESCRIPTOR ONLY
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 px-6 border-t border-white/[0.06]">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="text-[11px] font-mono tracking-[0.22em] text-emerald-400 mb-4">// WHAT TO DO THIS QUARTER</div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-5 leading-snug">
            Stop pretending more AI is the answer.<br />
            Promote deterministic math back to the places where decisions are made.
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            Or: skip the 24-month internal rebuild and license the discipline. The math is done.
            The verification is done. The contracts are live. The Yield Simulator is in production.
            The conversation we have been ready to have for a year is the one that turns your &ldquo;doesn&apos;t work&rdquo; column into &ldquo;where it works.&rdquo;
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/langchain"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 text-black font-semibold hover:brightness-110 transition"
              data-testid="banks-cta-bottom"
            >
              Request the Integration Brief <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="mailto:wayne@kojiru.com?subject=Kojiru%20for%20Banks%20%E2%80%94%20Architecture%20Conversation"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/15 hover:bg-white/[0.04] font-semibold transition"
              data-testid="banks-cta-email"
            >
              Email Wayne directly
            </a>
          </div>
          <div className="mt-10 flex items-center justify-center gap-6 text-xs text-zinc-500 font-mono">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> BaseScan source-verified</span>
            <span className="flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-cyan-400" /> Live on Base mainnet</span>
            <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-amber-400" /> GENIUS Act–aligned</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
